import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import TrainOutlinedIcon from '@mui/icons-material/TrainOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import { Navigate, useNavigate } from 'react-router-dom';
import { API_BASE_URL, getAuthHeaders, getCustomerId, getStoredUser } from './auth';

const countBy = (rows = [], key) =>
  rows.reduce((acc, row) => {
    const value = String(row?.[key] || 'Unknown');
    acc[value] = (acc[value] || 0) + 1;
    return acc;
  }, {});

const sumWeight = (rows = [], key) =>
  rows.reduce((acc, row) => acc + (Number(row?.[key]) || 0), 0);

const toLocalDateKey = (value) => {
  if (!value) return '';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '';
  const year = dt.getFullYear();
  const month = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatNumber = (value) => Number(value || 0).toLocaleString();
const isValidDate = (value) => !Number.isNaN(new Date(value).getTime());
const METRIC_TIME_ZONE = 'America/Chicago';
const getDateKeyInTimeZone = (value, timeZone = METRIC_TIME_ZONE) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const year = parts.find((p) => p.type === 'year')?.value;
  const month = parts.find((p) => p.type === 'month')?.value;
  const day = parts.find((p) => p.type === 'day')?.value;
  if (!year || !month || !day) return '';
  return `${year}-${month}-${day}`;
};
const isSameLocalDay = (start, end, timeZone = METRIC_TIME_ZONE) => {
  const startKey = getDateKeyInTimeZone(start, timeZone);
  const endKey = getDateKeyInTimeZone(end, timeZone);
  return Boolean(startKey && endKey && startKey === endKey);
};
const formatDuration = (minutes) => {
  const rounded = Math.max(0, Math.round(minutes || 0));
  if (rounded < 60) return `${rounded} min`;
  const hours = Math.floor(rounded / 60);
  const mins = rounded % 60;
  return mins === 0 ? `${hours}h` : `${hours}h ${mins}m`;
};
const DEFAULT_LOAD_GOAL_MINUTES = 90;
const LOAD_METER_MAX_MINUTES = 180;
const clampLoadGoalMinutes = (value, fallback = DEFAULT_LOAD_GOAL_MINUTES) => {
  const raw = Number(value);
  if (!Number.isFinite(raw)) return fallback;
  const rounded = Math.round(raw);
  return Math.min(Math.max(1, rounded), LOAD_METER_MAX_MINUTES);
};
const toConfiguredLoadGoalMinutes = () => clampLoadGoalMinutes(process.env.REACT_APP_LOAD_GOAL_MINUTES);
const formatGoalHoursLabel = (minutes) => {
  if (minutes % 60 === 0) return `${minutes / 60}h`;
  return `${(minutes / 60).toFixed(1)}h`;
};
const startOfDay = (value) => {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
};
const getBolCustomerId = (bol) => {
  const directCustomer = bol?.customerName;
  if (typeof directCustomer === 'string') return directCustomer;
  if (directCustomer?._id) return String(directCustomer._id);

  const orderCustomer = bol?.orderNumber?.customerName;
  if (typeof orderCustomer === 'string') return orderCustomer;
  if (orderCustomer?._id) return String(orderCustomer._id);

  return null;
};

const startOfWeek = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const buildWeekCalendar = (orders = []) => {
  const weekStart = startOfWeek(new Date());
  const days = Array.from({ length: 7 }).map((_, idx) => {
    const date = new Date(weekStart);
    date.setDate(weekStart.getDate() + idx);
    return {
      key: toLocalDateKey(date),
      date,
      label: date.toLocaleDateString(undefined, { weekday: 'short' }),
      shortDate: date.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' }),
      orders: [],
    };
  });

  const dayMap = new Map(days.map((d) => [d.key, d]));

  orders.forEach((order) => {
    const scheduled = order.pickUpDate || order.orderDate || order.deliveryDate;
    if (!scheduled) return;
    const key = toLocalDateKey(scheduled);
    const day = dayMap.get(key);
    if (!day) return;
    day.orders.push(order);
  });

  return days;
};

const buildShippedBolTrend = (bols = [], period = '30d') => {
  const today = startOfDay(new Date());
  let points = [];

  if (period === '1w' || period === '30d') {
    const days = period === '1w' ? 7 : 30;
    const firstDay = new Date(today);
    firstDay.setDate(today.getDate() - (days - 1));

    points = Array.from({ length: days }).map((_, idx) => {
      const d = new Date(firstDay);
      d.setDate(firstDay.getDate() + idx);
      return {
        key: d.toISOString().slice(0, 10),
        label: d.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' }),
        count: 0,
        isCurrent: d.toISOString().slice(0, 10) === today.toISOString().slice(0, 10),
      };
    });

    const pointByKey = new Map(points.map((p) => [p.key, p]));
    bols.forEach((bol) => {
      if (bol?.status !== 'Completed') return;
      const rawDate = bol.completedAt || bol.weighOutTime || bol.bolDate || bol.createdAt;
      if (!rawDate || !isValidDate(rawDate)) return;
      const key = startOfDay(rawDate).toISOString().slice(0, 10);
      const point = pointByKey.get(key);
      if (!point) return;
      point.count += 1;
    });
    return points;
  }

  if (period === 'ytd') {
    const year = today.getFullYear();
    const thisMonth = today.getMonth();
    points = Array.from({ length: thisMonth + 1 }).map((_, month) => {
      const d = new Date(year, month, 1);
      return {
        key: `${year}-${String(month + 1).padStart(2, '0')}`,
        label: d.toLocaleDateString(undefined, { month: 'short' }),
        count: 0,
        isCurrent: month === thisMonth,
      };
    });

    const pointByKey = new Map(points.map((p) => [p.key, p]));
    bols.forEach((bol) => {
      if (bol?.status !== 'Completed') return;
      const rawDate = bol.completedAt || bol.weighOutTime || bol.bolDate || bol.createdAt;
      if (!rawDate || !isValidDate(rawDate)) return;
      const d = new Date(rawDate);
      if (d.getFullYear() !== year) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const point = pointByKey.get(key);
      if (!point) return;
      point.count += 1;
    });
    return points;
  }

  const firstMonth = new Date(today.getFullYear(), today.getMonth() - 11, 1);
  points = Array.from({ length: 12 }).map((_, idx) => {
    const d = new Date(firstMonth.getFullYear(), firstMonth.getMonth() + idx, 1);
    return {
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString(undefined, { month: 'short' }),
      count: 0,
      isCurrent: d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth(),
    };
  });

  const pointByKey = new Map(points.map((p) => [p.key, p]));
  bols.forEach((bol) => {
    if (bol?.status !== 'Completed') return;
    const rawDate = bol.completedAt || bol.weighOutTime || bol.bolDate || bol.createdAt;
    if (!rawDate || !isValidDate(rawDate)) return;
    const d = new Date(rawDate);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const point = pointByKey.get(key);
    if (!point) return;
    point.count += 1;
  });
  return points;
};

const ShippedTrendAreaChart = ({ data = [], height = 180 }) => {
  if (!data.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
        No shipped BOL data in this period.
      </Typography>
    );
  }

  const width = 760;
  const padX = 42;
  const padTop = 12;
  const padBottom = 26;
  const maxY = Math.max(1, ...data.map((d) => Number(d.count) || 0));
  const baseY = height - padBottom;
  const drawableHeight = Math.max(1, baseY - padTop);
  const stepX = data.length > 1 ? (width - padX * 2) / (data.length - 1) : 0;

  const points = data.map((d, idx) => {
    const x = padX + idx * stepX;
    const y = baseY - ((Number(d.count) || 0) / maxY) * drawableHeight;
    return { ...d, x, y };
  });

  const linePath = points
    .map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(' ');
  const areaPath = `${linePath} L ${points[points.length - 1].x.toFixed(2)} ${baseY} L ${points[0].x.toFixed(2)} ${baseY} Z`;

  const tickCount = Math.min(6, data.length);
  const tickStep = Math.max(1, Math.ceil(data.length / tickCount));

  return (
    <Box sx={{ mt: 1.5 }}>
      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <Box sx={{ minWidth: 380 }}>
          <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height }}>
            {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
              const y = baseY - drawableHeight * ratio;
              return (
                <g key={ratio}>
                  <line
                    x1={padX}
                    y1={y}
                    x2={width - padX}
                    y2={y}
                    stroke="#ddd9d3"
                    strokeWidth="1"
                  />
                  <text
                    x={padX - 8}
                    y={y + 3}
                    fontSize="10"
                    fill="#5f6f7c"
                    textAnchor="end"
                  >
                    {Math.round(maxY * ratio)}
                  </text>
                </g>
              );
            })}

            <path d={areaPath} fill="rgba(17, 47, 71, 0.18)" />
            <path d={linePath} fill="none" stroke="#112f47" strokeWidth="2.4" />

            {points.map((p, idx) => (
              <circle
                key={`${p.key}-${idx}`}
                cx={p.x}
                cy={p.y}
                r={p.isCurrent ? 4 : 2.5}
                fill={p.isCurrent ? '#112f47' : '#274257'}
              />
            ))}

            {points
              .filter((_, idx) => idx % tickStep === 0 || idx === points.length - 1)
              .map((p, idx) => (
                <text
                  key={`label-${p.key}-${idx}`}
                  x={p.x}
                  y={height - 8}
                  fontSize="10"
                  fill="#5f6f7c"
                  textAnchor="middle"
                >
                  {p.label}
                </text>
              ))}
          </svg>
        </Box>
      </Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" color="text.secondary">
          Peak: {maxY}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Filled line trend of shipped BOL volume
        </Typography>
      </Stack>
    </Box>
  );
};

export default function Home() {
  const navigate = useNavigate();
  const user = useMemo(() => getStoredUser(), []);
  const customerId = useMemo(() => getCustomerId(user), [user]);
  const isCustomer = user?.role === 'customer';
  const isInternal = user?.role === 'internal' || user?.role === 'admin';

  const [loading, setLoading] = useState(Boolean(user));
  const [error, setError] = useState('');
  const [selectedLoadCustomerId, setSelectedLoadCustomerId] = useState('all');
  const [shippedTrendPeriod, setShippedTrendPeriod] = useState('30d');
  const [dashboard, setDashboard] = useState({
    orders: [],
    bols: [],
    railcars: [],
    groundInventoryLots: [],
    projects: [],
    receivers: [],
    shippers: [],
    materials: [],
    customers: [],
  });

  useEffect(() => {
    if (!user) return;
    if (isCustomer && !customerId) {
      setError('Customer scope is missing from your user profile. Please contact support.');
      setLoading(false);
      return;
    }

    const fetchWithTimeout = async (url, options = {}, timeoutMs = 15000) => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      try {
        const response = await fetch(url, { ...options, signal: controller.signal });
        if (!response.ok) {
          throw new Error(`Network response was not ok (${response.status}) for ${url}`);
        }
        return await response.json();
      } catch (err) {
        if (err.name === 'AbortError') {
          throw new Error(`Request timed out for ${url}`);
        }
        throw err;
      } finally {
        clearTimeout(timer);
      }
    };

    const customerParams = isCustomer ? new URLSearchParams({ customerId: String(customerId) }) : null;
    const scopedSuffix = customerParams ? `?${customerParams.toString()}` : '';

    const requestUrls = isCustomer
      ? [
          `${API_BASE_URL}/api/orders${scopedSuffix}`,
          `${API_BASE_URL}/api/bols${scopedSuffix}`,
          `${API_BASE_URL}/api/railcars${scopedSuffix}`,
          `${API_BASE_URL}/api/projects`,
          `${API_BASE_URL}/api/receivers`,
          `${API_BASE_URL}/api/shippers`,
          `${API_BASE_URL}/api/materials`,
        ]
      : [
          `${API_BASE_URL}/api/orders`,
          `${API_BASE_URL}/api/bols`,
          `${API_BASE_URL}/api/railcars`,
          `${API_BASE_URL}/api/customers`,
          `${API_BASE_URL}/api/projects`,
          `${API_BASE_URL}/api/receivers`,
          `${API_BASE_URL}/api/shippers`,
          `${API_BASE_URL}/api/materials`,
        ];

    setLoading(true);
    setError('');

    Promise.all(requestUrls.map((url) => fetchWithTimeout(url, { headers: getAuthHeaders() })))
      .then((dataSets) => {
        const [
          orders,
          bols,
          railcars,
          customersOrProjects,
          projectsOrReceivers,
          receiversOrShippers,
          shippersOrMaterials,
          maybeMaterials,
        ] = dataSets;

        const customers = isCustomer ? [] : customersOrProjects;
        const projects = isCustomer ? customersOrProjects : projectsOrReceivers;
        const receivers = isCustomer ? projectsOrReceivers : receiversOrShippers;
        const shippers = isCustomer ? receiversOrShippers : shippersOrMaterials;
        const materials = isCustomer ? shippersOrMaterials : maybeMaterials;

        setDashboard((prev) => ({
          ...prev,
          orders: Array.isArray(orders) ? orders : [],
          bols: Array.isArray(bols) ? bols : [],
          railcars: Array.isArray(railcars) ? railcars : [],
          customers: Array.isArray(customers) ? customers : [],
          projects: Array.isArray(projects) ? projects : [],
          receivers: Array.isArray(receivers) ? receivers : [],
          shippers: Array.isArray(shippers) ? shippers : [],
          materials: Array.isArray(materials) ? materials : [],
        }));
      })
      .catch((err) => {
        setError(err.message || 'Failed to load dashboard');
      })
      .finally(() => setLoading(false));
  }, [isCustomer, customerId, user]);

  useEffect(() => {
    if (!user) return;
    if (isCustomer && !customerId) return;

    const params = new URLSearchParams();
    if (isCustomer) params.set('customerId', String(customerId));

    fetch(`${API_BASE_URL}/api/ground-inventory${params.toString() ? `?${params.toString()}` : ''}`, {
      headers: getAuthHeaders(),
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload?.message || 'Failed to load ground inventory');
        return payload;
      })
      .then((payload) => {
        const lots = Array.isArray(payload?.lots) ? payload.lots : [];
        setDashboard((prev) => ({ ...prev, groundInventoryLots: lots }));
      })
      .catch(() => {
        setDashboard((prev) => ({ ...prev, groundInventoryLots: [] }));
      });
  }, [isCustomer, customerId, user]);

  useEffect(() => {
    if (!isInternal) return;
    if (selectedLoadCustomerId === 'all') return;
    const ids = new Set(dashboard.customers.map((c) => String(c._id)));
    if (!ids.has(String(selectedLoadCustomerId))) {
      setSelectedLoadCustomerId('all');
    }
  }, [dashboard.customers, isInternal, selectedLoadCustomerId]);

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  const companyName = user?.customer?.customerName || 'Customer';
  const customerLogo = user?.customer?.customerLogo || '';

  const orderCounts = countBy(dashboard.orders, 'orderStatus');
  const bolCounts = countBy(dashboard.bols, 'status');
  const railcarCounts = countBy(dashboard.railcars, 'currentStatus');
  const splitLoads = dashboard.bols.filter((b) => b.splitLoad).length;
  const totalRemainingWeight = sumWeight(dashboard.railcars, 'remainingWeight') + sumWeight(dashboard.groundInventoryLots, 'remainingWeight');
  const totalUnloadedWeight = sumWeight(dashboard.railcars, 'unloadedWeight');

  const recentOrders = [...dashboard.orders]
    .sort((a, b) => new Date(b.createdAt || b.orderDate || 0) - new Date(a.createdAt || a.orderDate || 0))
    .slice(0, 5);

  const recentBols = [...dashboard.bols]
    .sort((a, b) => new Date(b.completedAt || b.createdAt || 0) - new Date(a.completedAt || a.createdAt || 0))
    .slice(0, 5);
  const materialAvailability = (() => {
    const rows = new Map();

    dashboard.materials.forEach((material) => {
      const id = String(material?._id || '').trim();
      if (!id) return;
      rows.set(id, {
        id,
        name: material.materialName || 'Material',
        refNum: material.refNum || '',
        availableWeight: 0,
      });
    });

    dashboard.railcars.forEach((railcar) => {
      const materialId = String(railcar?.materialName?._id || railcar?.materialName || '').trim();
      if (!materialId) return;
      const remaining = Number(railcar?.remainingWeight);
      const fallbackWeight = Number(railcar?.reportedWeight);
      const usableWeight = Number.isFinite(remaining)
        ? Math.max(remaining, 0)
        : Number.isFinite(fallbackWeight)
          ? Math.max(fallbackWeight, 0)
          : 0;

      const existing = rows.get(materialId);
      if (existing) {
        existing.availableWeight += usableWeight;
      } else {
        rows.set(materialId, {
          id: materialId,
          name: railcar?.materialName?.materialName || 'Unmapped Material',
          refNum: railcar?.materialName?.refNum || '',
          availableWeight: usableWeight,
        });
      }
    });

    dashboard.groundInventoryLots.forEach((lot) => {
      const materialId = String(lot?.materialName?._id || lot?.materialName || '').trim();
      if (!materialId) return;
      const remaining = Number(lot?.remainingWeight);
      const usableWeight = Number.isFinite(remaining) ? Math.max(remaining, 0) : 0;

      const existing = rows.get(materialId);
      if (existing) {
        existing.availableWeight += usableWeight;
      } else {
        rows.set(materialId, {
          id: materialId,
          name: lot?.materialName?.materialName || 'Unmapped Material',
          refNum: lot?.materialName?.refNum || '',
          availableWeight: usableWeight,
        });
      }
    });

    return [...rows.values()].sort((a, b) => {
      if (b.availableWeight !== a.availableWeight) return b.availableWeight - a.availableWeight;
      return String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' });
    });
  })();

  const topStats = [
    {
      label: 'Orders',
      value: dashboard.orders.length,
      accent: '#112f47',
      icon: <LocalShippingOutlinedIcon />,
      helper: `${orderCounts.Submitted || 0} submitted`,
      href: '/orders',
    },
    {
      label: 'BOLs',
      value: dashboard.bols.length,
      accent: '#274257',
      icon: <ReceiptLongOutlinedIcon />,
      helper: `${bolCounts.Completed || 0} completed`,
      href: '/bols',
    },
    {
      label: 'Railcars',
      value: dashboard.railcars.length,
      accent: '#756f66',
      icon: <TrainOutlinedIcon />,
      helper: `${railcarCounts['On-Spot'] || 0} on spot`,
      href: '/railcars',
    },
    {
      label: 'Locations',
      value: dashboard.projects.length,
      accent: '#3b5367',
      icon: <Inventory2OutlinedIcon />,
      helper: `${dashboard.receivers.length} receivers`,
      href: '/projects',
    },
  ];
  const weeklyOrders = buildWeekCalendar(dashboard.orders);
  const openOrdersForDay = (dayKey) => {
    navigate(`/orders?date=${encodeURIComponent(dayKey)}`);
  };
  const todayKey = toLocalDateKey(new Date());

  const averageLoadStats = (() => {
    const scopedBols = dashboard.bols.filter((bol) => {
      if (bol?.status !== 'Completed') return false;
      if (!isValidDate(bol?.weighInTime) || !isValidDate(bol?.weighOutTime)) return false;
      if (!isSameLocalDay(bol.weighInTime, bol.weighOutTime, METRIC_TIME_ZONE)) return false;
      if (!isInternal) return true;
      if (selectedLoadCustomerId === 'all') return true;
      return getBolCustomerId(bol) === String(selectedLoadCustomerId);
    });

    if (scopedBols.length === 0) {
      return {
        hasData: false,
        sampleCount: 0,
        averageLoadMinutes: 0,
      };
    }

    const sumIn = scopedBols.reduce((acc, bol) => acc + new Date(bol.weighInTime).getTime(), 0);
    const sumOut = scopedBols.reduce((acc, bol) => acc + new Date(bol.weighOutTime).getTime(), 0);
    const avgInMs = sumIn / scopedBols.length;
    const avgOutMs = sumOut / scopedBols.length;
    const averageLoadMinutes = Math.max(0, (avgOutMs - avgInMs) / 60000);

    return {
      hasData: true,
      sampleCount: scopedBols.length,
      averageLoadMinutes,
    };
  })();

  const loadMinutes = averageLoadStats.averageLoadMinutes;
  const envLoadGoalMinutes = toConfiguredLoadGoalMinutes();
  const selectedCustomer = isInternal && selectedLoadCustomerId !== 'all'
    ? dashboard.customers.find((customer) => String(customer?._id) === String(selectedLoadCustomerId))
    : null;
  const activeCustomerForGoal = selectedCustomer || (!isInternal ? dashboard.customers[0] : null);
  const meterGoalMinutes = activeCustomerForGoal
    ? clampLoadGoalMinutes(activeCustomerForGoal.loadGoalMinutes, envLoadGoalMinutes)
    : envLoadGoalMinutes;
  const meterMaxMinutes = LOAD_METER_MAX_MINUTES;
  const meterPercent = Math.min(100, Math.max(0, (loadMinutes / meterMaxMinutes) * 100));
  const isGoalMet = loadMinutes <= meterGoalMinutes;
  const warningThresholdMinutes = meterGoalMinutes + ((meterMaxMinutes - meterGoalMinutes) / 2);
  const goalPercent = (meterGoalMinutes / meterMaxMinutes) * 100;
  const warningPercent = (warningThresholdMinutes / meterMaxMinutes) * 100;
  const meterBackgroundGradient = `linear-gradient(90deg, #16a34a 0%, #16a34a ${goalPercent}%, #cec8bf ${warningPercent}%, #dc2626 100%)`;
  const goalHoursLabel = formatGoalHoursLabel(meterGoalMinutes);
  const loadMeterBarColor = isGoalMet ? '#16a34a' : loadMinutes <= warningThresholdMinutes ? '#cec8bf' : '#dc2626';
  const scopedShippedBols = dashboard.bols.filter((bol) => {
    if (bol?.status !== 'Completed') return false;
    if (!isInternal) return true;
    if (selectedLoadCustomerId === 'all') return true;
    return getBolCustomerId(bol) === String(selectedLoadCustomerId);
  });
  const shippedBolTrend = buildShippedBolTrend(scopedShippedBols, shippedTrendPeriod);

  if (isInternal) {
    const orderCounts = countBy(dashboard.orders, 'orderStatus');
    const bolCounts = countBy(dashboard.bols, 'status');
    const railcarCounts = countBy(dashboard.railcars, 'currentStatus');
    const internalStats = [
      {
        label: 'Submitted Orders',
        value: orderCounts.Submitted || 0,
        accent: '#112f47',
        icon: <LocalShippingOutlinedIcon />,
        helper: `${dashboard.orders.length} total orders`,
        href: '/orders?status=open',
      },
      {
        label: 'Draft BOL Queue',
        value: bolCounts.Draft || 0,
        accent: '#b45309',
        icon: <ReceiptLongOutlinedIcon />,
        helper: `${bolCounts.Completed || 0} completed`,
        href: '/bols',
      },
      {
        label: 'Railcars',
        value: dashboard.railcars.length,
        accent: '#274257',
        icon: <TrainOutlinedIcon />,
        helper: `${railcarCounts['On-Spot'] || 0} on spot`,
        href: '/railcars',
      },
      {
        label: 'Customers',
        value: dashboard.customers.length,
        accent: '#3b5367',
        icon: <Groups2OutlinedIcon />,
        helper: `${dashboard.projects.length} locations`,
        href: '/customers',
      },
    ];

    return (
      <Box
        sx={{
          background:
            'radial-gradient(circle at 10% 0%, rgba(17, 47, 71, 0.09), transparent 45%), radial-gradient(circle at 92% 8%, rgba(206, 200, 191, 0.45), transparent 35%), #ffffff',
          borderRadius: 2,
          p: { xs: 1.5, md: 2.5 },
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            mb: 2.5,
            borderRadius: 2.5,
            color: '#ffffff',
            background:
              'linear-gradient(115deg, #112f47 0%, #274257 55%, #112f47 100%)',
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
            <Box>
              <Typography variant="overline" sx={{ opacity: 0.9, letterSpacing: 1.2 }}>
                Internal Operations
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                WTNN Dispatch Dashboard
              </Typography>
              <Typography sx={{ opacity: 0.85, mt: 0.5 }}>
                Monitor submitted orders, draft BOL workload, and railcar position across all customers.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button variant="contained" onClick={() => navigate('/orders?status=open')} sx={{ backgroundColor: '#cec8bf', color: '#112f47', '&:hover': { backgroundColor: '#bdb5aa' } }}>
                Open Orders
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => navigate('/bols')}
                sx={{
                  borderColor: '#ffffff',
                  color: '#ffffff',
                  backgroundColor: 'transparent',
                  '&:hover': { borderColor: '#ffffff', backgroundColor: 'rgba(255,255,255,0.12)' },
                }}
              >
                Open BOL Queue
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => navigate('/railcars')}
                sx={{
                  borderColor: '#ffffff',
                  color: '#ffffff',
                  backgroundColor: 'transparent',
                  '&:hover': { borderColor: '#ffffff', backgroundColor: 'rgba(255,255,255,0.12)' },
                }}
              >
                Open Railcars
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {internalStats.map((stat) => (
            <Grid item xs={12} sm={6} lg={3} key={stat.label}>
              <Card sx={{ borderRadius: 2, border: '1px solid var(--ui-border)' }}>
                <CardActionArea
                  onClick={() => navigate(stat.href)}
                  sx={{
                    borderRadius: 2,
                    '&:hover': { backgroundColor: '#ffffff' },
                  }}
                >
                  <CardContent sx={{ py: 1.8 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2" color="text.secondary">
                        {stat.label}
                      </Typography>
                      <Box sx={{ color: stat.accent }}>{stat.icon}</Box>
                    </Stack>
                    <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 700 }}>
                      {formatNumber(stat.value)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stat.helper}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={1.5}>
          <Grid item xs={12} lg={7}>
            <Card sx={{ borderRadius: 2, mb: 1.5, border: '1px solid var(--ui-border)' }}>
              <CardContent>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={1.2}>
                  <Box>
                    <Typography variant="h6">Average Time to Load</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Average Weigh Out Time minus Average Weigh In Time (same-day loads only)
                    </Typography>
                  </Box>
                  <FormControl size="small" sx={{ minWidth: 240 }}>
                    <InputLabel id="avg-load-customer-label">Customer Scope</InputLabel>
                    <Select
                      labelId="avg-load-customer-label"
                      label="Customer Scope"
                      value={selectedLoadCustomerId}
                      onChange={(e) => setSelectedLoadCustomerId(e.target.value)}
                    >
                      <MenuItem value="all">All Customers</MenuItem>
                      {dashboard.customers.map((customer) => (
                        <MenuItem key={customer._id} value={String(customer._id)}>
                          {customer.customerName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>

                {averageLoadStats.hasData ? (
                  <>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mt: 1.25 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Average Time to Load
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: loadMeterBarColor }}>
                          {formatDuration(loadMinutes)}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={isGoalMet ? `In Goal (<= ${goalHoursLabel})` : `Above Goal (> ${goalHoursLabel})`}
                        color={isGoalMet ? 'success' : 'warning'}
                        variant={isGoalMet ? 'filled' : 'outlined'}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Based on {averageLoadStats.sampleCount} same-day completed BOLs ({METRIC_TIME_ZONE})
                      </Typography>
                    </Stack>
                    <Box sx={{ mt: 1.5 }}>
                      <Box
                        sx={{
                          position: 'relative',
                          height: 12,
                          borderRadius: 999,
                          background: meterBackgroundGradient,
                          opacity: 0.26,
                        }}
                      />
                      <Box
                        sx={{
                          mt: -1.5,
                          width: `${meterPercent}%`,
                          height: 12,
                          borderRadius: 999,
                          backgroundColor: loadMeterBarColor,
                          transition: 'width 0.35s ease',
                        }}
                      />
                      <Stack direction="row" justifyContent="space-between" mt={0.6}>
                        <Typography variant="caption" color="text.secondary">0m</Typography>
                        <Typography variant="caption" color="text.secondary">{`Goal ${goalHoursLabel}`}</Typography>
                        <Typography variant="caption" color="text.secondary">3h+</Typography>
                      </Stack>
                    </Box>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.2 }}>
                    No completed BOLs with weigh-in and weigh-out times for this scope.
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Shipped BOLs Over Time
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Completed BOL shipment trend by selected period.
                    </Typography>
                  </Box>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel id="shipped-trend-period-label">Period</InputLabel>
                    <Select
                      labelId="shipped-trend-period-label"
                      label="Period"
                      value={shippedTrendPeriod}
                      onChange={(e) => setShippedTrendPeriod(e.target.value)}
                    >
                      <MenuItem value="1w">1 Week</MenuItem>
                      <MenuItem value="30d">30 Days</MenuItem>
                      <MenuItem value="ytd">YTD</MenuItem>
                      <MenuItem value="1y">1 Year</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
                <Box sx={{ mt: 1.5, overflowX: 'auto' }}>
                  <ShippedTrendAreaChart data={shippedBolTrend} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Card sx={{ borderRadius: 2, mb: 1.5 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  System Status
                </Typography>
                <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                  <Chip label={`Orders: ${dashboard.orders.length}`} size="small" />
                  <Chip label={`BOLs: ${dashboard.bols.length}`} size="small" />
                  <Chip label={`Railcars: ${dashboard.railcars.length}`} size="small" />
                  <Chip label={`Customers: ${dashboard.customers.length}`} size="small" />
                </Stack>
                <Divider sx={{ my: 1.2 }} />
                <Stack spacing={0.7}>
                  <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Locations</Typography><Typography variant="body2" fontWeight={600}>{dashboard.projects.length}</Typography></Stack>
                  <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Receivers</Typography><Typography variant="body2" fontWeight={600}>{dashboard.receivers.length}</Typography></Stack>
                  <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Shippers</Typography><Typography variant="body2" fontWeight={600}>{dashboard.shippers.length}</Typography></Stack>
                  <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Materials</Typography><Typography variant="body2" fontWeight={600}>{dashboard.materials.length}</Typography></Stack>
                </Stack>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Next Actions
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button size="small" variant="outlined" onClick={() => navigate('/orders?status=open')}>Review Submitted Orders</Button>
                  <Button size="small" variant="outlined" onClick={() => navigate('/bols')}>Complete Draft BOLs</Button>
                  <Button size="small" variant="outlined" onClick={() => navigate('/railcars/unloads')}>Audit Railcar Unloads</Button>
                </Stack>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 2, mt: 1.5 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Weekly Order Calendar
                </Typography>
                <Grid container spacing={0.8}>
                  {weeklyOrders.map((day) => (
                    <Grid item xs={12} sm={6} key={day.key}>
                    <Paper
                      variant="outlined"
                      onClick={() => openOrdersForDay(day.key)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          openOrdersForDay(day.key);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        cursor: 'pointer',
                        transition: 'background-color var(--ui-ease), border-color var(--ui-ease), box-shadow var(--ui-ease)',
                        backgroundColor:
                          day.key === todayKey ? '#ece7df' : '#fff',
                        borderColor:
                          day.key === todayKey ? '#112f47' : 'var(--ui-border)',
                        '&:hover': {
                          backgroundColor: day.key === todayKey ? '#e6ded3' : '#f8fafc',
                          borderColor: '#112f47',
                          boxShadow: 'var(--ui-shadow-sm)',
                        },
                      }}
                    >
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle2">{day.label}</Typography>
                          <Chip size="small" label={day.orders.length} color={day.orders.length > 0 ? 'info' : 'default'} />
                        </Stack>
                        <Typography variant="caption" color="text.secondary">{day.shortDate}</Typography>
                        <Stack spacing={0.4} mt={0.7}>
                          {day.orders.length === 0 ? (
                            <Typography variant="caption" color="text.secondary">No scheduled orders</Typography>
                          ) : (
                            day.orders.slice(0, 2).map((order) => (
                              <Typography key={order._id} variant="caption" sx={{ display: 'block' }}>
                                {order.orderNumber || 'Order'} - {order.customerName?.customerName || 'Customer'}
                              </Typography>
                            ))
                          )}
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>

          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background:
          'radial-gradient(circle at 10% 0%, rgba(17, 47, 71, 0.13), transparent 42%), radial-gradient(circle at 85% 10%, rgba(206, 200, 191, 0.45), transparent 35%), #ffffff',
        borderRadius: 2,
        p: { xs: 1.5, md: 2.5 },
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 2.5,
          borderRadius: 2.5,
          color: '#ffffff',
          background:
            'linear-gradient(115deg, #112f47 0%, #274257 48%, #cec8bf 100%)',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
          <Box>
            <Typography variant="overline" sx={{ opacity: 0.9, letterSpacing: 1.2 }}>
              Customer Dashboard
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {companyName}
            </Typography>
            <Typography sx={{ opacity: 0.85, mt: 0.5 }}>
              Orders, BOL performance, and railcar position in one place.
            </Typography>
          </Box>
          <Box sx={{ ml: { md: 'auto' }, width: { xs: '100%', md: 'auto' } }}>
            {customerLogo ? (
              <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, mb: 1.25 }}>
                <Box
                  component="img"
                  src={customerLogo}
                  alt={companyName}
                  sx={{ maxHeight: 54, maxWidth: 180, objectFit: 'contain', opacity: 0.95 }}
                />
              </Box>
            ) : null}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              <Button variant="contained" onClick={() => navigate('/orders/create')} sx={{ backgroundColor: '#cec8bf', color: '#112f47', '&:hover': { backgroundColor: '#bdb5aa' } }}>
                New Order
              </Button>
              <Button variant="outlined" onClick={() => navigate('/railcars')} sx={{ borderColor: 'rgba(255,255,255,0.45)', color: '#ffffff' }}>
                View Railcars
              </Button>
              <Button variant="outlined" onClick={() => navigate('/bols')} sx={{ borderColor: 'rgba(255,255,255,0.45)', color: '#ffffff' }}>
                View BOLs
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Paper>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {topStats.map((stat) => (
          <Grid item xs={12} sm={6} lg={3} key={stat.label}>
            <Card sx={{ borderRadius: 2, border: '1px solid var(--ui-border)' }}>
              <CardActionArea
                onClick={() => navigate(stat.href)}
                sx={{
                  borderRadius: 2,
                  '&:hover': { backgroundColor: '#ffffff' },
                }}
              >
                <CardContent sx={{ py: 1.8 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2" color="text.secondary">
                      {stat.label}
                    </Typography>
                    <Box sx={{ color: stat.accent }}>{stat.icon}</Box>
                  </Stack>
                  <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 700 }}>
                    {formatNumber(stat.value)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stat.helper}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={1.5}>
        <Grid item xs={12} lg={8}>
          <Card sx={{ borderRadius: 2, mb: 1.5, border: '1px solid var(--ui-border)' }}>
            <CardContent>
              <Typography variant="h6">Average Time to Load</Typography>
              <Typography variant="body2" color="text.secondary">
                Average Weigh Out Time minus Average Weigh In Time (same-day loads only)
              </Typography>
              {averageLoadStats.hasData ? (
                <>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mt: 1.25 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Average Time to Load
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: loadMeterBarColor }}>
                        {formatDuration(loadMinutes)}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={isGoalMet ? `In Goal (<= ${goalHoursLabel})` : `Above Goal (> ${goalHoursLabel})`}
                      color={isGoalMet ? 'success' : 'warning'}
                      variant={isGoalMet ? 'filled' : 'outlined'}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Based on {averageLoadStats.sampleCount} same-day completed BOLs ({METRIC_TIME_ZONE})
                    </Typography>
                  </Stack>
                  <Box sx={{ mt: 1.5 }}>
                    <Box
                      sx={{
                        position: 'relative',
                        height: 12,
                        borderRadius: 999,
                        background: meterBackgroundGradient,
                        opacity: 0.26,
                      }}
                    />
                    <Box
                      sx={{
                        mt: -1.5,
                        width: `${meterPercent}%`,
                        height: 12,
                        borderRadius: 999,
                        backgroundColor: loadMeterBarColor,
                        transition: 'width 0.35s ease',
                      }}
                    />
                    <Stack direction="row" justifyContent="space-between" mt={0.6}>
                      <Typography variant="caption" color="text.secondary">0m</Typography>
                      <Typography variant="caption" color="text.secondary">{`Goal ${goalHoursLabel}`}</Typography>
                      <Typography variant="caption" color="text.secondary">3h+</Typography>
                    </Stack>
                  </Box>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.2 }}>
                  No completed BOLs with weigh-in and weigh-out times yet.
                </Typography>
              )}
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Operational Snapshot
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 1.5 }}>
                    <Typography variant="subtitle2">Order Status</Typography>
                    <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap mt={1}>
                      <Chip label={`Draft ${orderCounts.Draft || 0}`} size="small" color="warning" />
                      <Chip label={`Submitted ${orderCounts.Submitted || 0}`} size="small" color="info" />
                      <Chip label={`Shipped ${orderCounts.Shipped || 0}`} size="small" color="success" />
                      <Chip label={`Cancelled ${orderCounts.Cancelled || 0}`} size="small" color="error" variant="outlined" />
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 1.5 }}>
                    <Typography variant="subtitle2">BOL Status</Typography>
                    <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap mt={1}>
                      <Chip label={`Draft ${bolCounts.Draft || 0}`} size="small" color="warning" />
                      <Chip label={`Completed ${bolCounts.Completed || 0}`} size="small" color="success" />
                      <Chip label={`Split Loads ${splitLoads}`} size="small" color="primary" variant="outlined" />
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 1.5 }}>
                    <Typography variant="subtitle2">Railcar Position</Typography>
                    <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap mt={1}>
                      <Chip label={`On Spot ${railcarCounts['On-Spot'] || 0}`} size="small" color="success" />
                      <Chip label={`Available ${railcarCounts.Available || 0}`} size="small" color="info" />
                      <Chip label={`Inbound ${railcarCounts.Inbound || 0}`} size="small" color="warning" variant="outlined" />
                      <Chip label={`Released ${railcarCounts.Released || 0}`} size="small" />
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ my: 1.5 }} />
              <Grid container spacing={1}>
                <Grid item xs={12} md={6}>
                  <Paper
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      background: 'linear-gradient(135deg, #ece7df 0%, #ffffff 100%)',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Remaining Railcar Weight
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#112f47' }}>
                      {formatNumber(totalRemainingWeight)} lbs
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      background: 'linear-gradient(135deg, #f4f1eb 0%, #ffffff 100%)',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Total Unloaded Weight
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#756f66' }}>
                      {formatNumber(totalUnloadedWeight)} lbs
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 2, mt: 1.5 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Weekly Order Calendar
              </Typography>
              <Grid container spacing={1}>
                {weeklyOrders.map((day) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={day.key}>
                    <Paper
                      variant="outlined"
                      onClick={() => openOrdersForDay(day.key)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          openOrdersForDay(day.key);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        cursor: 'pointer',
                        transition: 'background-color var(--ui-ease), border-color var(--ui-ease), box-shadow var(--ui-ease)',
                        backgroundColor:
                          day.key === todayKey ? '#ece7df' : '#fff',
                        borderColor:
                          day.key === todayKey ? '#112f47' : 'var(--ui-border)',
                        '&:hover': {
                          backgroundColor: day.key === todayKey ? '#e6ded3' : '#f8fafc',
                          borderColor: '#112f47',
                          boxShadow: 'var(--ui-shadow-sm)',
                        },
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle2">{day.label}</Typography>
                        <Chip size="small" label={day.orders.length} color={day.orders.length > 0 ? 'info' : 'default'} />
                      </Stack>
                      <Typography variant="caption" color="text.secondary">{day.shortDate}</Typography>
                      <Stack spacing={0.4} mt={0.7}>
                        {day.orders.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">No scheduled orders</Typography>
                        ) : (
                          day.orders.slice(0, 2).map((order) => (
                            <Typography key={order._id} variant="caption" sx={{ display: 'block' }}>
                              {order.orderNumber || 'Order'} - {order.receiverName?.receiverName || 'Receiver'}
                            </Typography>
                          ))
                        )}
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 2, mt: 1.5 }}>
            <CardContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Shipped BOLs Over Time
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed BOL shipment trend by selected period.
                  </Typography>
                </Box>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel id="shipped-trend-period-label-customer">Period</InputLabel>
                  <Select
                    labelId="shipped-trend-period-label-customer"
                    label="Period"
                    value={shippedTrendPeriod}
                    onChange={(e) => setShippedTrendPeriod(e.target.value)}
                  >
                    <MenuItem value="1w">1 Week</MenuItem>
                    <MenuItem value="30d">30 Days</MenuItem>
                    <MenuItem value="ytd">YTD</MenuItem>
                    <MenuItem value="1y">1 Year</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <Box sx={{ mt: 1.5, overflowX: 'auto' }}>
                <ShippedTrendAreaChart data={shippedBolTrend} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card sx={{ borderRadius: 2, mb: 1.5 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Network Setup
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Customer-side reference data currently configured.
              </Typography>
              <Stack spacing={0.7}>
                <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Locations</Typography><Typography variant="body2" fontWeight={600}>{dashboard.projects.length}</Typography></Stack>
                <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Receivers</Typography><Typography variant="body2" fontWeight={600}>{dashboard.receivers.length}</Typography></Stack>
                <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Shippers</Typography><Typography variant="body2" fontWeight={600}>{dashboard.shippers.length}</Typography></Stack>
                <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Materials</Typography><Typography variant="body2" fontWeight={600}>{dashboard.materials.length}</Typography></Stack>
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 2, mb: 1.5 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Material Availability
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Net weight currently available by material.
              </Typography>
              {materialAvailability.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No material-linked railcars available yet.
                </Typography>
              ) : (
                <Stack spacing={0.7}>
                  {materialAvailability.map((row) => (
                    <Stack
                      key={row.id}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ borderBottom: '1px dashed var(--ui-border)', pb: 0.5 }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.name}
                        </Typography>
                        {row.refNum ? (
                          <Typography variant="caption" color="text.secondary">
                            Ref: {row.refNum}
                          </Typography>
                        ) : null}
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {formatNumber(row.availableWeight)} lbs
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Activity
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0.3 }}>
                Latest Orders
              </Typography>
              <Stack spacing={0.6} mt={0.8}>
                {recentOrders.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No recent orders.</Typography>
                ) : (
                  recentOrders.map((order) => (
                    <Stack key={order._id} direction="row" justifyContent="space-between" sx={{ borderBottom: '1px dashed var(--ui-border)', pb: 0.5 }}>
                      <Typography variant="body2">{order.orderNumber || 'Order'}</Typography>
                      <Typography variant="caption" color="text.secondary">{order.orderStatus || 'Draft'}</Typography>
                    </Stack>
                  ))
                )}
              </Stack>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1.2 }}>
                Latest BOLs
              </Typography>
              <Stack spacing={0.6} mt={0.8}>
                {recentBols.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No recent BOLs.</Typography>
                ) : (
                  recentBols.map((bol) => (
                    <Stack key={bol._id} direction="row" justifyContent="space-between" sx={{ borderBottom: '1px dashed var(--ui-border)', pb: 0.5 }}>
                      <Typography variant="body2">{bol.orderNumber?.orderNumber || 'BOL'}</Typography>
                      <Typography variant="caption" color="text.secondary">{bol.status || 'Draft'}</Typography>
                    </Stack>
                  ))
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

    </Box>
  );
}

